// CARO. Casa de estilo — size variants of a product (metadata.sizes / sizeStock).
//
// Leaf module (re-exported by `@/lib/cart`) so `@/lib/catalog/availability`
// can use it without the cycle availability -> cart -> shipping -> availability.

import type { Product } from '@/types';

/**
 * The product's size variants (`metadata.sizes`), cleaned: trimmed, non-empty,
 * at most 8 characters (the cart line limit), no `"` (the size becomes a quoted
 * JSON-path label in `decrementStock`), deduplicated, in order. `[]` means
 * the product is unsized — no size is asked for and none is recorded.
 */
export function productSizes(p: Pick<Product, 'metadata'>): string[] {
  const raw = p.metadata.sizes;
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== 'string') continue;
    const s = v.trim();
    if (s && s.length <= 8 && !s.includes('"') && !out.includes(s)) out.push(s);
  }
  return out;
}

/**
 * Units on hand for one size, or null when the product has no `sizeStock` map
 * (a size missing from the map counts as 0). Shared by the Worker (priceCart)
 * and the client cart (`availableForSize`).
 */
export function sizeUnits(p: Pick<Product, 'metadata'>, size: string): number | null {
  const map = p.metadata.sizeStock;
  if (!map || typeof map !== 'object' || Array.isArray(map)) return null;
  const n = (map as Record<string, unknown>)[size];
  return typeof n === 'number' && Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/**
 * Sum of the per-size units of a product that tracks stock per size (sizes +
 * `sizeStock`), or null when it doesn't. Its `stock_quantity` must equal this
 * sum: every admin write path (grid / patch / CSV) checks it on the final row.
 */
export function sizeStockTotal(p: Pick<Product, 'metadata'>): number | null {
  const sizes = productSizes(p);
  if (sizes.length === 0 || sizes.every((s) => sizeUnits(p, s) === null)) return null;
  return sizes.reduce((n, s) => n + (sizeUnits(p, s) ?? 0), 0);
}
